import { useState, useEffect } from 'react';

/**
 * Custom hook that debounces a value
 * 
 * @param value - The value to debounce
 * @param delay - The debounce delay in milliseconds
 * @returns The debounced value
 * 
 * @example
 * const [email, setEmail] = useState('');
 * const debouncedEmail = useDebounce(email, 500);
 * 
 * useEffect(() => {
 *   if (debouncedEmail) {
 *     // API call will only happen 500ms after user stops typing
 *     checkEmailExists(debouncedEmail);
 *   }
 * }, [debouncedEmail]);
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set a timeout to update the debounced value
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup: clear the timeout if value changes before delay completes
    // This prevents multiple API calls during rapid typing
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default useDebounce;
